import { useMutationState } from '@tanstack/react-query';
import { DropdownMenu } from 'cpool-components/input/DropdownMenu';
import { LOAN_REQUEST_OFFER_SOURCES, LOAN_REQUEST_OFFER_STATUS, MUTATION_KEYS } from 'cpool-constants';
import type { LOAN_REQUEST_OFFER_SOURCE, LOAN_REQUEST_OFFER_STATUS_TYPES } from 'cpool-constants';
import type { SchemaLoanRequestResolver_GetAcceptedOffers } from 'cpool-tables/backend-resolvers';
import { objectKeys } from 'cpool-utils';
import { useTranslation } from 'react-i18next';

import { OfferTableCell } from './OfferTableCell';
import { useOfferStatusMutation } from '~/features/leadDetails/components/Overview/OfferManagement/hooks/useOfferStatusMutation';

type Props = {
    loanRequestId: string;
    source: LOAN_REQUEST_OFFER_SOURCE;
    id: string;
    leadId: string;
    disabled?: boolean;
    status: SchemaLoanRequestResolver_GetAcceptedOffers[number]['status'];
};

export const Status: React.FC<Props> = ({ disabled = false, id, leadId, loanRequestId, source, status }) => {
    const { t } = useTranslation();
    const queryMutator = useOfferStatusMutation({ id, leadId, loanRequestId, source });
    const pendingMutations = useMutationState({
        filters: { mutationKey: [MUTATION_KEYS.CANCEL_CONSORS_SUBSCRIPTION, leadId], status: 'pending' },
    });

    const isPending = pendingMutations.length > 0;

    let statusDisplay: string | null = null;
    if (isPending && status !== LOAN_REQUEST_OFFER_STATUS.CANCELLED) {
        statusDisplay = t('OFFER_MANAGEMENT.STATUS.CANCEL_PENDING');
    } else if (status) {
        statusDisplay = t(`OFFER_MANAGEMENT.STATUS.${status}`);
    }

    if (source === LOAN_REQUEST_OFFER_SOURCES.MANUAL)
        return (
            // The options panel is portalled, but React still bubbles its clicks to the row's onClick.
            <OfferTableCell className="w-44" onClick={(event) => event.stopPropagation()}>
                <DropdownMenu
                    data-testid={`offer-status-select-${id}`}
                    disabled={disabled || queryMutator.isPending}
                    eventStopPropagation
                    matchTriggerWidth
                    onChange={(selected) => queryMutator.mutate(selected as LOAN_REQUEST_OFFER_STATUS_TYPES)}
                    options={objectKeys(LOAN_REQUEST_OFFER_STATUS).map((offerStatus) => ({
                        label: t(`OFFER_MANAGEMENT.STATUS.${offerStatus}`),
                        value: offerStatus,
                    }))}
                    placeholder=""
                    scrollable
                    showBorder
                    triggerClassName="w-full justify-between"
                    value={status ?? undefined}
                />
            </OfferTableCell>
        );

    return (
        <OfferTableCell className="w-36">
            <span>{statusDisplay}</span>
        </OfferTableCell>
    );
};
